import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';

// Generic comment thread: one table (entity_type + entity_id) backs every
// caller. The API keeps an allowlist of entity types; a type outside it is
// refused (400), so the list here mirrors it.
export type CommentEntityType =
  | 'budget_item' | 'event' | 'guest' | 'procurement_item' | 'procurement_order'
  | 'shipment' | 'shipping_agent' | 'task' | 'team_member' | 'vendor' | 'venue'
  | 'asoebi_order' | 'asoebi_assignment' | 'asoebi_delivery' | 'vendor_quote'
  | 'payment' | 'bundle' | 'product';

/**
 * Threads that routinely quote amounts and terms. The API closes them (403) to
 * anyone who may not see money, because a comment body cannot be redacted field
 * by field; the screens do not offer them at all.
 */
const MONEY_THREADS: ReadonlySet<string> = new Set([
  'budget_item', 'procurement_order', 'procurement_item', 'payment', 'vendor_quote', 'asoebi_order', 'bundle', 'product',
]);

export function isMoneyThread(entityType: string): boolean {
  return MONEY_THREADS.has(entityType);
}

/** True when this person must not see the thread (it is about money and they may not see money). */
export function commentsAreClosedTo(entityType: string, canViewFinancials: boolean): boolean {
  return isMoneyThread(entityType) && !canViewFinancials;
}

export interface EntityComment {
  /** uuid, kept as text. */
  id: string;
  /** Display name; falls back to the e-mail. */
  author: string;
  /** Who wrote it. Only this person may edit or delete it; null for comments carried over without an e-mail. */
  authorEmail: string | null;
  body: string;
  createdAtISO: string;
  updatedAtISO: string | null;
}

interface ApiComment {
  id: string;
  entity_type: string;
  entity_id: string;
  author: string;
  author_email?: string | null;
  body: string;
  created_at: string;
  updated_at: string | null;
}

function mapApiComment(row: ApiComment): EntityComment {
  return {
    id: String(row.id),
    author: row.author,
    authorEmail: row.author_email ?? null,
    body: row.body,
    createdAtISO: row.created_at,
    updatedAtISO: row.updated_at,
  };
}

export async function loadComments(entityType: CommentEntityType, entityId: string): Promise<EntityComment[]> {
  const res = await apiGet<{ comments: ApiComment[] }>(
    planningPath(`/comments?entity_type=${entityType}&entity_id=${encodeURIComponent(entityId)}`)
  );
  return (res?.comments ?? []).map(mapApiComment);
}

export async function addComment(entityType: CommentEntityType, entityId: string, body: string): Promise<EntityComment> {
  const created = await apiPost<ApiComment>(planningPath('/comments'), { entity_type: entityType, entity_id: entityId, body });
  return mapApiComment(created);
}

export async function updateComment(commentId: string, body: string): Promise<EntityComment> {
  const updated = await apiPut<ApiComment>(planningPath(`/comments/${commentId}`), { body });
  return mapApiComment(updated);
}

export async function deleteComment(commentId: string): Promise<void> {
  await apiDelete(planningPath(`/comments/${commentId}`));
}
